import React, { useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useGroupsQuery } from '../hooks/queries/useGroupQueries';

interface GroupFilterProps {
  selectedGroupIds: Set<string>;
  onGroupIdsChange: (groupIds: Set<string>) => void;
  onCreateGroup?: () => void;
  isMobile?: boolean;
}

// 将颜色转换为浅色背景色
const getLightBackgroundColor = (color: string): string => {
  // 如果是十六进制颜色
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    // 使用 15% 不透明度创建浅色背景
    return `rgba(${r}, ${g}, ${b}, 0.15)`;
  }
  // 默认浅色背景
  return 'rgba(59, 130, 246, 0.15)';
};

const GroupFilter: React.FC<GroupFilterProps> = ({ 
  selectedGroupIds, 
  onGroupIdsChange, 
  onCreateGroup: _onCreateGroup,
  isMobile: _isMobile = false 
}) => {
  const { user } = useAuth();
  const { data: groups = [], isLoading: loading } = useGroupsQuery({ enabled: !!user });
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleGroupToggle = (groupId: string) => {
    const newSelectedIds = new Set(selectedGroupIds);
    if (newSelectedIds.has(groupId)) {
      newSelectedIds.delete(groupId);
    } else {
      newSelectedIds.add(groupId);
    }
    onGroupIdsChange(newSelectedIds);
  };

  const handleSelectAll = () => {
    if (selectedGroupIds.size === groups.length) {
      // 如果全部选中，则清空
      onGroupIdsChange(new Set());
    } else {
      // 否则选中全部
      onGroupIdsChange(new Set(groups.map(g => g.id)));
    }
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
        加载中...
      </div>
    );
  }

  return (
    <div 
      ref={scrollContainerRef}
      className="group-filter-scroll overflow-x-auto overflow-y-hidden scrollbar-hide px-2 py-1.5"
      style={{
        WebkitOverflowScrolling: 'touch',
        scrollBehavior: 'smooth',
        willChange: 'scroll-position',
        overscrollBehaviorX: 'contain',
      }}
    >
      <div className="flex items-center gap-2 min-w-max">
        {/* 全部按钮 */}
        {groups.length > 1 && (
          <button
            onClick={handleSelectAll}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all flex-shrink-0 whitespace-nowrap ${
              selectedGroupIds.size === groups.length
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
              selectedGroupIds.size === groups.length
                ? 'bg-green-600 dark:bg-green-500'
                : 'bg-gray-400 dark:bg-gray-500'
            }`}>
              {selectedGroupIds.size === groups.length ? (
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            <span>全部</span>
          </button>
        )}

        {/* 分组标签 - Tag 样式 */}
        {groups.map((group) => {
          const isSelected = selectedGroupIds.has(group.id);
          const groupColor = group.color || '#3B82F6'; // 默认蓝色
          const lightBgColor = getLightBackgroundColor(groupColor);
          
          // 根据分组颜色确定文字颜色（确保对比度）
          const getTextColor = (color: string): string => {
            if (color.startsWith('#')) {
              const hex = color.slice(1);
              const r = parseInt(hex.slice(0, 2), 16);
              const g = parseInt(hex.slice(2, 4), 16);
              const b = parseInt(hex.slice(4, 6), 16);
              // 计算亮度，如果颜色较深则使用原色，否则使用深色版本
              const brightness = (r * 299 + g * 587 + b * 114) / 1000;
              return brightness > 128 ? color : color;
            }
            return color;
          };
          
          const textColor = isSelected ? getTextColor(groupColor) : '#6B7280';
          
          return (
            <button
              key={group.id}
              onClick={() => handleGroupToggle(group.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition-all duration-200 flex-shrink-0 whitespace-nowrap cursor-pointer border backdrop-blur-sm ${
                isSelected
                  ? 'border-current shadow-sm'
                  : 'bg-white/60 dark:bg-white/10 border-white/30 dark:border-white/10 hover:bg-white/80 dark:hover:bg-white/20'
              }`}
              style={isSelected ? { backgroundColor: lightBgColor, color: textColor } : { color: 'var(--muted-foreground)' }}
            >
              {/* 圆形图标 */}
              <div 
                className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: groupColor }}
              >
                {isSelected ? (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <div className="w-2 h-2 rounded-full bg-white/60" />
                )}
              </div>
              <span>{group.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default GroupFilter;
